import { useState, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Book,
  Cloud,
  Shield,
  FileText,
  Users,
  ChevronRight,
  ChevronDown,
  Search,
  CheckSquare,
  AlertTriangle,
  Activity,
  Skull,
  Building2,
  Radar,
  Server,
  Settings,
  LucideIcon,
} from 'lucide-react'
import {
  knowledgeBase,
  searchArticles,
  KnowledgeArticle,
  KnowledgeSection,
} from '../knowledge'

// Icon mapping from string to component
const iconMap: Record<string, LucideIcon> = {
  Book,
  Cloud,
  Shield,
  FileText,
  Users,
  CheckSquare,
  AlertTriangle,
  Activity,
  Skull,
  Building2,
  Radar,
  Server,
  Settings,
}

// Get icon component from string name
function getIcon(iconName: string): LucideIcon {
  return iconMap[iconName] || Book
}

export default function Help() {
  const [searchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSection, setSelectedSection] = useState<string | null>(
    knowledgeBase.sections[0]?.id || null
  )
  const [selectedArticle, setSelectedArticle] = useState<string | null>(
    knowledgeBase.sections[0]?.articles[0]?.id || null
  )
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () => new Set(knowledgeBase.sections.slice(0, 2).map((s) => s.id))
  )

  // Handle article query parameter from URL
  useEffect(() => {
    const articleId = searchParams.get('article')
    if (articleId) {
      // Find the article and its section
      for (const section of knowledgeBase.sections) {
        const article = section.articles.find((a) => a.id === articleId)
        if (article) {
          setSelectedSection(section.id)
          setSelectedArticle(article.id)
          setExpandedSections((prev) => new Set([...prev, section.id]))
          break
        }
      }
    }
  }, [searchParams])

  // Search filtering
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) {
      return knowledgeBase.sections
    }

    const results = searchArticles(searchQuery)
    const resultIds = new Set(results.map((r) => r.id))

    return knowledgeBase.sections
      .map((section) => ({
        ...section,
        articles: section.articles.filter((a) => resultIds.has(a.id)),
      }))
      .filter((section) => section.articles.length > 0)
  }, [searchQuery])

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev)
      if (next.has(sectionId)) {
        next.delete(sectionId)
      } else {
        next.add(sectionId)
      }
      return next
    })
  }

  const selectArticle = (sectionId: string, articleId: string) => {
    setSelectedSection(sectionId)
    setSelectedArticle(articleId)
    if (!expandedSections.has(sectionId)) {
      setExpandedSections((prev) => new Set([...prev, sectionId]))
    }
  }

  // Find current article
  const currentSection = knowledgeBase.sections.find(
    (s) => s.id === selectedSection
  )
  const currentArticle = currentSection?.articles.find(
    (a) => a.id === selectedArticle
  )

  return (
    <div className="flex h-[calc(100vh-8rem)]">
      {/* Sidebar */}
      <div className="w-72 border-r border-gray-200 bg-white overflow-y-auto flex-shrink-0">
        <div className="p-4">
          <h1 className="text-xl font-bold text-gray-900 mb-4">
            Help & Documentation
          </h1>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search docs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Navigation */}
          <nav className="space-y-1">
            {filteredSections.map((section) => {
              const IconComponent = getIcon(section.icon)
              return (
                <div key={section.id}>
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
                  >
                    <span className="flex items-center gap-2">
                      <IconComponent className="h-4 w-4" />
                      {section.title}
                    </span>
                    {expandedSections.has(section.id) ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  {expandedSections.has(section.id) && (
                    <div className="ml-6 mt-1 space-y-1">
                      {section.articles.map((article) => (
                        <button
                          key={article.id}
                          onClick={() => selectArticle(section.id, article.id)}
                          className={`w-full text-left px-3 py-1.5 text-sm rounded-lg ${
                            selectedSection === section.id &&
                            selectedArticle === article.id
                              ? 'bg-primary-50 text-primary-700 font-medium'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {article.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="max-w-3xl mx-auto p-8">
          {currentArticle ? (
            <>
              <div className="mb-6">
                <p className="text-sm text-gray-500 mb-1">
                  {currentSection?.title}
                </p>
                <h1 className="text-2xl font-bold text-gray-900">
                  {currentArticle.title}
                </h1>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <ArticleContent content={currentArticle.content} />
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <Book className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Select an article from the sidebar</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Markdown content renderer (same as HelpModal but can be more featured here)
function ArticleContent({ content }: { content: string }) {
  const lines = content.split('\n')
  const elements: JSX.Element[] = []
  let inList = false
  let listItems: string[] = []
  let listType: 'ul' | 'ol' = 'ul'
  let inTable = false
  let tableRows: string[][] = []

  const flushList = () => {
    if (listItems.length > 0) {
      if (listType === 'ol') {
        elements.push(
          <ol
            key={`list-${elements.length}`}
            className="list-decimal list-inside space-y-1 my-3 text-gray-600"
          >
            {listItems.map((item, i) => (
              <li key={i}>{formatInlineText(item)}</li>
            ))}
          </ol>
        )
      } else {
        elements.push(
          <ul
            key={`list-${elements.length}`}
            className="list-disc list-inside space-y-1 my-3 text-gray-600"
          >
            {listItems.map((item, i) => (
              <li key={i}>{formatInlineText(item)}</li>
            ))}
          </ul>
        )
      }
      listItems = []
    }
    inList = false
  }

  const flushTable = () => {
    if (tableRows.length > 1) {
      const headers = tableRows[0]
      // Filter out separator rows
      const body = tableRows.slice(1).filter((row) => row.length > 0)
      elements.push(
        <div key={`table-${elements.length}`} className="overflow-x-auto my-4">
          <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50">
              <tr>
                {headers.map((h, i) => (
                  <th
                    key={i}
                    className="px-4 py-2 text-left font-semibold text-gray-700 border-b"
                  >
                    {h.trim()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, i) => (
                <tr
                  key={i}
                  className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                >
                  {row.map((cell, j) => (
                    <td
                      key={j}
                      className="px-4 py-2 border-b border-gray-100 text-gray-600"
                    >
                      {formatInlineText(cell.trim())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
      tableRows = []
    }
    inTable = false
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Table detection
    if (line.startsWith('|')) {
      if (!inTable) {
        flushList()
        inTable = true
      }
      // Skip separator rows (|---|---|)
      if (line.match(/^\|[-:| ]+\|$/)) {
        continue
      }
      const cells = line
        .split('|')
        .slice(1, -1)
        .filter((c) => c.trim() !== '')
      if (cells.length > 0) {
        tableRows.push(cells)
      }
      continue
    } else if (inTable) {
      flushTable()
    }

    // Headers
    if (line.startsWith('# ')) {
      flushList()
      elements.push(
        <h1
          key={i}
          className="text-2xl font-bold text-gray-900 mt-6 mb-4 first:mt-0"
        >
          {line.slice(2)}
        </h1>
      )
    } else if (line.startsWith('## ')) {
      flushList()
      elements.push(
        <h2 key={i} className="text-xl font-semibold text-gray-900 mt-6 mb-3">
          {line.slice(3)}
        </h2>
      )
    } else if (line.startsWith('### ')) {
      flushList()
      elements.push(
        <h3 key={i} className="text-lg font-semibold text-gray-900 mt-5 mb-2">
          {line.slice(4)}
        </h3>
      )
    } else if (line.startsWith('#### ')) {
      flushList()
      elements.push(
        <h4 key={i} className="text-base font-semibold text-gray-900 mt-4 mb-2">
          {line.slice(5)}
        </h4>
      )
    }
    // List items (unordered)
    else if (line.startsWith('- ') || line.startsWith('* ')) {
      if (!inList || listType !== 'ul') {
        flushList()
        inList = true
        listType = 'ul'
      }
      listItems.push(line.slice(2))
    }
    // Numbered lists
    else if (line.match(/^\d+\.\s/)) {
      if (!inList || listType !== 'ol') {
        flushList()
        inList = true
        listType = 'ol'
      }
      listItems.push(line.replace(/^\d+\.\s/, ''))
    }
    // Empty line
    else if (line.trim() === '') {
      flushList()
    }
    // Regular paragraph
    else if (line.trim()) {
      flushList()
      elements.push(
        <p key={i} className="text-gray-600 my-3 leading-relaxed">
          {formatInlineText(line)}
        </p>
      )
    }
  }

  flushList()
  flushTable()

  return <div className="prose prose-sm max-w-none">{elements}</div>
}

// Format inline markdown (bold, code, etc.)
function formatInlineText(text: string): React.ReactNode {
  const parts: React.ReactNode[] = []
  let remaining = text
  let key = 0

  while (remaining.length > 0) {
    // Bold
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/)
    // Code
    const codeMatch = remaining.match(/`(.+?)`/)

    // Find the earliest match
    let earliest: { type: 'bold' | 'code'; match: RegExpMatchArray } | null =
      null

    if (boldMatch && boldMatch.index !== undefined) {
      earliest = { type: 'bold', match: boldMatch }
    }
    if (codeMatch && codeMatch.index !== undefined) {
      if (!earliest || codeMatch.index < earliest.match.index!) {
        earliest = { type: 'code', match: codeMatch }
      }
    }

    if (earliest) {
      const before = remaining.slice(0, earliest.match.index)
      if (before) parts.push(before)

      if (earliest.type === 'bold') {
        parts.push(
          <strong key={key++} className="font-semibold text-gray-900">
            {earliest.match[1]}
          </strong>
        )
      } else {
        parts.push(
          <code
            key={key++}
            className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800"
          >
            {earliest.match[1]}
          </code>
        )
      }
      remaining = remaining.slice(
        earliest.match.index! + earliest.match[0].length
      )
    } else {
      parts.push(remaining)
      break
    }
  }

  return parts.length > 0 ? parts : text
}
